import { EmptyState, ErrorBoundary, FlexBox } from '@dixa/lib-shared-habla';
import type {
  Controls,
  InstrumentPanel as InstrumentPanelT,
  ReadInstrumentsQueryFn,
} from 'lib-analytics-types';
import { isResponseWithData } from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';
import isEqual from 'lodash/isEqual';
import { CSSProperties, memo, useCallback, useMemo } from 'react';
import { useMount, useUpdateEffect } from 'react-use';

import { AnalyticsControls } from 'analytics/components/controls/analyticsControls';
import { InstrumentPanel } from 'analytics/components/panels/components/panel/components/panel/instrumentPanel';
import { InstrumentRenderer } from 'analytics/components/panels/components/panel/components/renderer/instrumentRenderer';
import { useAnalyticsState } from 'analytics/components/panels/utils/useAnalyticsState/useAnalyticsState';
import { analyticsApi } from 'apis/analytics';

import { type UsePersistInstrumentSettingsResult } from '../../../../utils/usePersistInstrumentSettings';
import { InstrumentRendererError } from '../../../renderer/components/error/instrumentRendererError';
import {
  InstrumentComparateSide,
  InstrumentComparate as InstrumentComparateT,
} from '../../instrumentComparison.types';

import styles from './instrumentComparate.module.scss';

export type InstrumentComparateProps = {
  /** The instrument panel. */
  panel: InstrumentPanelT;
  /** The side of the comparison. */
  side: InstrumentComparateSide;
  /** The comparate state. */
  comparate: InstrumentComparateT;
  /** Callback to update a comparate. */
  onUpdate: (
    side: InstrumentComparateSide,
    comparate: InstrumentComparateT,
  ) => void;
  /** Custom styles passed to the panel element. */
  panelStyle?: CSSProperties;
};

export const InstrumentComparate = memo(function InstrumentComparate({
  side,
  panel: initialPanel,
  comparate: { controls },
  onUpdate,
  panelStyle,
}: InstrumentComparateProps): JSX.Element {
  const panel = useMemo(
    (): InstrumentPanelT => ({ ...initialPanel, controls }),
    [initialPanel, controls],
  );

  const { dashboard } = useAnalyticsState(panel);

  const [
    readInstrumentsQuery,
    { instruments, isLoading, isFetching, isError },
  ] = analyticsApi.useLazyReadComparisonInstrumentsQuery({
    selectFromResult: ({ data, ...rest }) => ({
      ...rest,
      instruments: data?.data.instruments ?? [],
    }),
  });

  /** Fetches the list of instruments for the panel. */
  const readInstruments: ReadInstrumentsQueryFn = useCallback(
    async (arg) => {
      const response = await readInstrumentsQuery({
        side,
        payload: {
          id: panel.id,
          ...controls,
          meta: { ...arg?.meta },
        },
      });

      if (isResponseWithData(response)) {
        return response.data;
      }
    },
    [controls, panel.id, readInstrumentsQuery, side],
  );

  // Preloads the data on mount for the left side of the comparison.
  useMount(() => {
    if (side === 'left') readInstruments();
  });

  // Updates the instrument data when the comparate controls change.
  useUpdateEffect(() => {
    readInstruments();
  }, [controls]);

  const handleChangeControls = useCallback(
    (updatedControls: Partial<Controls>) => {
      onUpdate(side, {
        controls: {
          ...controls,
          ...updatedControls,
        },
      });

      if (
        updatedControls.context &&
        !isEqual(controls.context, updatedControls.context)
      ) {
        ampli.analyticsCompareContextUpdated({
          dashboard: dashboard.title,
          panel: panel.title,
          side,
        });
      }

      if (
        updatedControls.period &&
        !isEqual(controls.period, updatedControls.period)
      ) {
        ampli.analyticsComparePeriodUpdated({
          dashboard: dashboard.title,
          panel: panel.title,
          side,
          timezone:
            updatedControls.settings?.timezone ?? controls.settings.timezone,
          value:
            updatedControls.period.values.length === 1
              ? updatedControls.period.values[0].value
              : 'dateWithDefaultTime',
          withSchedule: !!updatedControls.settings?.schedule_id,
        });
      }
    },
    [controls, dashboard.title, onUpdate, panel.title, side],
  );

  const handleSetInstrumentSetting: UsePersistInstrumentSettingsResult['setInstrumentSetting'] =
    useCallback(
      (key, value) => {
        handleChangeControls({
          settings: {
            ...controls.settings,
            [key]: value,
          },
        });
      },
      [controls.settings, handleChangeControls],
    );

  const isEmpty =
    instruments.length === 0 && !isLoading && !isFetching && !isError;

  return (
    <div className={styles.root}>
      <AnalyticsControls
        panel={panel}
        initiallyCollapsedFilters
        onChangeControls={handleChangeControls}
      />

      <InstrumentPanel panel={panel} showHeader={false} style={panelStyle}>
        {isEmpty ? (
          <FlexBox flex="1" justifyContent="center" alignItems="center">
            <EmptyState
              title="Nothing to compare"
              description="Select something above to see the comparison."
              icon="exchange"
            />
          </FlexBox>
        ) : (
          <ErrorBoundary fallback={<InstrumentRendererError />}>
            <InstrumentRenderer
              panel={panel}
              instruments={instruments}
              isFetching={isFetching}
              isError={isError}
              onReadInstruments={readInstruments}
              onSetInstrumentSetting={handleSetInstrumentSetting}
            />
          </ErrorBoundary>
        )}
      </InstrumentPanel>
    </div>
  );
});
